import React, { useState } from "react";
import { Sparkles, Check, ShieldCheck, Heart, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Pula Founding Member — £3/month recurring subscription via Stripe.
 * Used to fund the mission during the pre-FCA-authorisation phase.
 * Positioned as a community subscription (not an investment product).
 */
export default function FoundingMember() {
  const [f, setF] = useState({ email: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState("");

  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });

  /**
   * Try to redirect the user to Stripe checkout in the most robust way possible.
   * Order:
   *   1. window.top.location — breaks out of iframes (Emergent preview mobile wrapper)
   *   2. window.open with _blank — new tab
   *   3. window.location.href — same tab fallback
   * If none succeed within 800ms, we surface the URL as a visible link the user
   * can click manually. This is critical for mobile preview wrappers and any
   * embedded context where the auto-redirect can get trapped.
   */
  const goToCheckout = (url) => {
    setCheckoutUrl(url);
    // First try: top-level redirect (escapes iframes)
    try {
      if (window.top && window.top !== window) {
        window.top.location.href = url;
        return;
      }
    } catch (_) {
      // Cross-origin frame — cannot access window.top. Fall through.
    }
    // Second try: open in a new tab (safe in embedded contexts)
    const opened = window.open(url, "_blank", "noopener,noreferrer");
    if (opened) return;
    // Final fallback: same-tab redirect
    window.location.href = url;
  };

  const subscribe = async () => {
    if (!f.email) { toast.error("Please share your email so we know it's you."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/subscriptions/checkout", {
        lookup_key: "pula_founding_monthly",
        origin_url: window.location.origin,
        email: f.email,
        name: f.name,
      });
      goToCheckout(data.checkout_url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout — please try again.");
      setBusy(false);
    }
  };

  return (
    <section className="py-16 md:py-24 border-t mv-hairline" data-testid="founding-member"
             style={{ background: "linear-gradient(180deg, #fff 0%, var(--mv-bg) 100%)" }}>
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ Pula Founding Member</span></div>
        <h2 className="font-display text-3xl md:text-5xl mv-green-text mt-3 leading-[1.05]">
          Stand with us <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>from the very first drop.</span>
        </h2>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          {/* Left: pitch */}
          <div className="lg:col-span-3 space-y-5">
            <p className="text-[15.5px] leading-relaxed" style={{ color: "var(--mv-ink)" }}>
              Every big journey starts with a few brave people who believe before there's proof. If you'd like to help us open the doors — legally, honestly, and one country at a time — become a <strong>Pula Founding Member</strong>. £3 a month. Cancel anytime. No commitment beyond one cup of tea a month.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { i: <Sparkles size={16}/>, t: "Founding Member badge", d: "Visible across Pula and Mavula for as long as you support us." },
                { i: <MessageCircle size={16}/>, t: "Direct line to Sebata",   d: "Personal replies to your questions — not a helpdesk." },
                { i: <Check size={16}/>,        t: "First-look invitations",   d: "New country pilots, new features, real project openings." },
                { i: <Heart size={16}/>,        t: "Impact updates",          d: "Quarterly notes on what your support helped build." },
              ].map((c) => (
                <div key={c.t} className="mv-card p-4">
                  <div className="w-8 h-8 rounded-2xl flex items-center justify-center mv-green-text mb-2" style={{ background: "rgba(212,175,55,0.16)" }}>{c.i}</div>
                  <div className="font-display text-[14.5px] mv-green-text">{c.t}</div>
                  <p className="text-[12.5px] mt-0.5" style={{ color: "var(--mv-muted)" }}>{c.d}</p>
                </div>
              ))}
            </div>

            <div className="mv-card p-4 flex items-start gap-3" style={{ background: "rgba(10,59,40,0.04)" }}>
              <ShieldCheck size={18} className="mt-0.5 shrink-0 mv-green-text"/>
              <div className="text-[13px]" style={{ color: "var(--mv-ink)" }}>
                <strong>What this is not:</strong> This is a community subscription — <strong>not an investment</strong>, not a loan, not a promise of returns. It funds our work. You are not buying property with this subscription. If you're here for property, please <a href="/waiting-list" className="underline mv-green-text">join the waiting list</a> instead.
              </div>
            </div>
          </div>

          {/* Right: subscribe card */}
          <div className="lg:col-span-2">
            <div className="mv-card p-6 md:p-7">
              <div className="flex items-baseline gap-1">
                <span className="font-display text-5xl mv-green-text">£3</span>
                <span className="text-[13px]" style={{ color: "var(--mv-muted)" }}>/ month</span>
              </div>
              <div className="text-[12.5px] mt-1" style={{ color: "var(--mv-muted)" }}>Cancel any time · Secure Stripe checkout</div>

              <div className="mt-5 space-y-3">
                <label className="block">
                  <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Your name</span>
                  <input value={f.name} onChange={on("name")} autoComplete="name"
                         data-testid="fm-name"
                         className="mt-1 w-full px-3 py-2.5 rounded-xl border mv-hairline text-[14.5px]"/>
                </label>
                <label className="block">
                  <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Email *</span>
                  <input type="email" value={f.email} onChange={on("email")} required autoComplete="email"
                         data-testid="fm-email"
                         className="mt-1 w-full px-3 py-2.5 rounded-xl border mv-hairline text-[14.5px]"/>
                </label>
              </div>

              <button
                type="button"
                onClick={subscribe}
                disabled={busy || !f.email}
                data-testid="fm-subscribe"
                className="mt-5 btn-gold w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy ? <><Loader2 size={14} className="animate-spin"/> Redirecting…</> : <><Sparkles size={14}/> Become a Founding Member</>}
              </button>

              {checkoutUrl && (
                <div className="mt-3 p-3 rounded-xl text-[12.5px] text-center" style={{ background: "rgba(212,175,55,0.10)", color: "var(--mv-ink)" }} data-testid="fm-fallback-link-row">
                  Didn't get redirected?{" "}
                  <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" className="underline mv-green-text font-semibold" data-testid="fm-fallback-link">
                    Tap here to open Stripe checkout →
                  </a>
                </div>
              )}

              <div className="mt-3 text-[11px] text-center" style={{ color: "var(--mv-muted)" }}>
                Payments processed by Stripe. Mavula never sees your card details.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
